import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { getExchange, setIdTokenGetter } from '../lib/session';
import { requireEmailLogin, staffLoginOptions } from '../lib/staff-login';

interface Profile {
  id: string;
  organization_id: string | null;
  display_name: string | null;
  email: string | null;
  role: 'customer' | 'employee' | 'agent' | 'supervisor' | 'admin' | 'owner';
  status: string;
  avatar_url: string | null;
}

interface AuthContextType {
  user: { sub: string; email?: string; name?: string } | null;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  signIn: (email?: string) => Promise<void>;
  signOut: () => void;
  isAdmin: boolean;
  isStaff: boolean;
}

// Agents answer conversations; supervisors and above also administer.
const STAFF_ROLES = ['agent', 'supervisor', 'admin', 'owner'];

const AuthContext = createContext<AuthContextType | null>(null);

function AuthContextProvider({ children }: { children: ReactNode }) {
  const {
    user: auth0User,
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
    logout,
    getIdTokenClaims,
    error: auth0Error,
  } = useAuth0();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (isAuthenticated && auth0User?.sub) {
      // The ID token authenticates edge-function calls; auth-exchange turns it
      // into the profile (looked up by external_id, not by Auth0 sub) and a
      // Supabase token for RLS-scoped table reads.
      setIdTokenGetter(async () => (await getIdTokenClaims())?.__raw ?? null);
      setProfileLoading(true);
      setProfile(null);
      setError(null);
      getExchange()
        .then(({ profile: profileData }) => {
          if (cancelled) return;
          if (!STAFF_ROLES.includes(profileData.role)) {
            setError('Access denied. This inbox is for Avala support staff.');
            setProfile(null);
          } else {
            setProfile(profileData as Profile);
          }
        })
        .catch((err: Error) => {
          if (cancelled) return;
          setError(err.message);
          setProfile(null);
        })
        .finally(() => { if (!cancelled) setProfileLoading(false); });
    } else if (!isAuthenticated && !auth0Loading) {
      setIdTokenGetter(null);
      setProfile(null);
      setProfileLoading(false);
    }
    return () => {
      cancelled = true;
      setIdTokenGetter(null);
    };
  }, [isAuthenticated, auth0User?.sub, auth0Loading, getIdTokenClaims]);

  const signIn = useCallback(async (email?: string) => {
    setError(null);
    if (email !== undefined) await requireEmailLogin(supportApiUrl);
    await loginWithRedirect(staffLoginOptions(email));
  }, [loginWithRedirect]);

  const handleSignOut = useCallback(() => {
    setProfile(null);
    setIdTokenGetter(null);
    logout({
      logoutParams: {
        returnTo: window.location.origin + '/login',
      },
    });
  }, [logout]);

  const loading = auth0Loading || profileLoading;
  const isAdmin = profile ? ['admin', 'owner', 'supervisor'].includes(profile.role) : false;
  const isStaff = profile ? STAFF_ROLES.includes(profile.role) : false;

  const user = isAuthenticated && auth0User
    ? { sub: auth0User.sub!, email: auth0User.email, name: auth0User.name }
    : null;

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      error: error || (auth0Error ? auth0Error.message : null),
      signIn,
      signOut: handleSignOut,
      isAdmin,
      isStaff,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN || '';
const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID || '';
const supportApiUrl = import.meta.env.VITE_SUPABASE_URL || import.meta.env.NEXT_PUBLIC_SUPABASE_URL || '';

export function AuthProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Retire the disconnected embedded-OTP cache without ever reading tokens.
    try {
      localStorage.removeItem('support_id_token');
      localStorage.removeItem('support_access_token');
    } catch {
      // Disabled browser storage must not prevent SDK-managed login.
    }
  }, []);
  // Guard: if Auth0 is not configured, render children without Auth0Provider
  // This prevents a crash when env vars aren't set (e.g., first deploy)
  if (!auth0Domain || !auth0ClientId) {
    return (
      <AuthContext.Provider value={{
        user: null,
        profile: null,
        loading: false,
        error: 'Staff sign-in is not configured. Please contact your administrator.',
        signIn: async () => { throw new Error('Staff sign-in is not configured. Please contact your administrator.'); },
        signOut: () => { console.warn('Auth0 not configured'); },
        isAdmin: false,
        isStaff: false,
      }}>
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: 'openid profile email',
      }}
    >
      <AuthContextProvider>
        {children}
      </AuthContextProvider>
    </Auth0Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
